import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
// AI Chatbot temporarily disabled - uncomment to re-enable
// import { MascotChatButton } from "@/components/mascot/MascotChatButton";
import { TabBar } from "@/components/layout/TabBar";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { OnboardingRoute } from "@/components/OnboardingRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { useAuth, AuthProvider } from "@/hooks/useAuth";
import { TourProvider } from "@/contexts/TourContext";
import { GuestProvider } from "@/contexts/GuestContext";
import { MembershipProvider } from "@/contexts/MembershipContext";
import { LocationPermissionProvider } from "@/hooks/useLocationPermission";
import { TourPromptDialog } from "@/components/tour/TourPromptDialog";
import { SpotlightOverlay } from "@/components/tour/SpotlightOverlay";
import { TaskGuideOverlay } from "@/components/tour/TaskGuideOverlay";
import { Suspense, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useAppUpdater } from '@/hooks/useAppUpdater';
import { AppUpdateToast } from '@/components/ui/AppUpdateToast';
import { useHerdRealtimeUpdates } from '@/hooks/useHerdRealtimeUpdates';
import { Skeleton } from "@/components/ui/skeleton";
import ChunkErrorBoundary from "@/components/errors/ChunkErrorBoundary";
import { AppBootWatchdog } from "@/components/AppBootWatchdog";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { logSecurityEvent, getCachedUserId } from "@/utils/securityLogger";
import RouteErrorBoundary from "@/components/errors/RouteErrorBoundary";
import { useSlowLoadWatchdog } from "@/hooks/useSlowLoadWatchdog";

import { normalizeError, describeError } from "@/utils/normalizeError";
import { supabase } from "@/integrations/supabase/client";

// Eager loaded pages (critical path)
import Auth from "./pages/Auth";
import Home from "./pages/Home";

// Lazy loaded pages with retry logic for iOS chunk loading issues
const Events = lazyWithRetry(() => import("./pages/Events"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const Schedule = lazyWithRetry(() => import("./pages/Schedule"));
const Herds = lazyWithRetry(() => import("./pages/Herds"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const PasswordReset = lazyWithRetry(() => import("./pages/PasswordReset"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const CreateHerdRequest = lazyWithRetry(() => import("./pages/CreateHerdRequest"));
const HostEventRequest = lazyWithRetry(() => import("./pages/HostEventRequest"));
const HerdDetail = lazyWithRetry(() => import("./pages/HerdDetail"));
const HerdThread = lazyWithRetry(() => import("./pages/HerdThread"));
const EventDetail = lazyWithRetry(() => import("./pages/EventDetail"));
const UserProfile = lazyWithRetry(() => import("./pages/UserProfile"));
const Friends = lazyWithRetry(() => import("./pages/Friends"));
const DirectConversation = lazyWithRetry(() => import("./pages/DirectConversation"));
const Photos = lazyWithRetry(() => import("./pages/Photos"));
const PhotoDetail = lazyWithRetry(() => import("./pages/PhotoDetail"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const HallOfFame = lazyWithRetry(() => import("./pages/HallOfFame"));
const Shop = lazyWithRetry(() => import("./pages/Shop"));
const NotificationSettings = lazyWithRetry(() => import("./pages/NotificationSettings"));
const AccountSuspended = lazyWithRetry(() => import("./pages/AccountSuspended"));
const GuestEventLanding = lazyWithRetry(() => import("./pages/GuestEventLanding"));
const WaiverRecordPage = lazyWithRetry(() => import("./pages/WaiverRecordPage"));
const GuestEventSuccess = lazyWithRetry(() => import("./pages/GuestEventSuccess"));

// Admin pages (lazy loaded with retry)
const AdminLayout = lazyWithRetry(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminEvents = lazyWithRetry(() => import("./pages/admin/AdminEvents"));
const AdminEventRequests = lazyWithRetry(() => import("./pages/admin/AdminEventRequests"));
const AdminHerds = lazyWithRetry(() => import("./pages/admin/AdminHerds"));
const AdminUserRequests = lazyWithRetry(() => import("./pages/admin/AdminUserRequests"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers"));
const AdminModeration = lazyWithRetry(() => import("./pages/admin/AdminModeration"));
const AdminScrapers = lazyWithRetry(() => import("./pages/admin/AdminScrapers"));
const AdminLogs = lazyWithRetry(() => import("./pages/admin/AdminLogs"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const AdminPaidEvents = lazyWithRetry(() => import("./pages/admin/AdminPaidEvents"));
const AdminPhotos = lazyWithRetry(() => import("./pages/admin/AdminPhotos"));
const AdminDataAudit = lazyWithRetry(() => import("./pages/admin/AdminDataAudit"));
const AdminHerdAnalytics = lazyWithRetry(() => import("./pages/admin/AdminHerdAnalytics"));
const AdminSubscriptions = lazyWithRetry(() => import("./pages/admin/AdminSubscriptions"));
const AdminSecurityEvents = lazyWithRetry(() => import("./pages/admin/AdminSecurityEvents"));
const AdminEmails = lazyWithRetry(() => import("./pages/admin/AdminEmails"));
const AdminWaivers = lazyWithRetry(() => import("./pages/admin/AdminWaivers"));
const AdminWaiverRecords = lazyWithRetry(() => import("./pages/admin/AdminWaiverRecords"));
const AdminNotificationDelivery = lazyWithRetry(() => import("./pages/admin/AdminNotificationDelivery"));
// AdminStagedEvents removed - staged events are now shown in AdminEvents

// Helper to get current user ID for logging (cached — never blocks logging on a network call)
const getCurrentUserId = (): string | null => getCachedUserId();

const isNetworkFailure = (normalized: ReturnType<typeof normalizeError>): boolean => {
  const msg = (normalized.error_message || '').toLowerCase();
  return (
    (typeof navigator !== 'undefined' && navigator.onLine === false) ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('timeout') ||
    msg.includes('aborted')
  );
};

// Global query error handler
const queryCache = new QueryCache({
  onError: async (error, query) => {
    const normalized = normalizeError(error);

    // Skip logging for expected auth errors
    if (normalized.error_message === 'User not authenticated') {
      return;
    }

    const userId = getCurrentUserId();
    const queryKeyStr = JSON.stringify(query.queryKey).slice(0, 100);

    logSecurityEvent(
      isNetworkFailure(normalized) ? 'client_network_timeout' : 'client_query_error',
      userId,
      `Query failed: ${queryKeyStr} — ${describeError(normalized)}`,
      {
        ...normalized,
        query_key: queryKeyStr,
        page_url: window.location.pathname,
        online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      }
    );
  },
});

// Global mutation error handler
const mutationCache = new MutationCache({
  onError: async (error, _variables, _context, mutation) => {
    const userId = getCurrentUserId();
    const mutationKey = mutation.options.mutationKey 
      ? JSON.stringify(mutation.options.mutationKey).slice(0, 100) 
      : 'unnamed';

    const normalized = normalizeError(error);
    logSecurityEvent(
      'client_mutation_error',
      userId,
      `Mutation failed: ${mutationKey} — ${describeError(normalized)}`,
      {

        ...normalized,
        mutation_key: mutationKey,
        page_url: window.location.pathname,
      }
    );
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute - keeps data fresh longer during navigation
      gcTime: 1000 * 60 * 10, // 10 minutes cache
      refetchOnMount: false, // Don't refetch when remounting - prevents flash during quick navigation
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Loading fallback component
// Reports a Slow Load event if the page never finishes loading (blank/stuck screen).
const PageLoader = () => {
  useSlowLoadWatchdog(true, 'route page load');
  return (

  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-full max-w-md px-6 space-y-4">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  </div>
  );
};


const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateAvailable, forceUpdate } = useAppUpdater();
  const showTabBar = !["/onboarding", "/auth", "/account-suspended"].includes(location.pathname) && !location.pathname.startsWith("/admin") && !location.pathname.startsWith("/messages/") && !location.pathname.startsWith("/g/");

  // Enable real-time herd updates at app level (single subscription)
  useHerdRealtimeUpdates();

  // Idempotent OneSignal registration: register once per user per session.
  // Prevents StrictMode double-mount and route-remount duplicate registrations
  // that cause users to receive notifications twice.
  const registeredUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.id) return;

    // Skip if we already registered this user in this session.
    const sessionKey = `onesignal_registered_user`;
    const alreadyRegistered =
      registeredUserIdRef.current === user.id ||
      sessionStorage.getItem(sessionKey) === user.id;

    if (alreadyRegistered) return;

    registeredUserIdRef.current = user.id;
    try { sessionStorage.setItem(sessionKey, user.id); } catch {}

    import('despia-native').then(({ default: despia }) => {
      despia(`setonesignalplayerid://?user_id=${user.id}`);
    }).catch((err) => {
      // Native bridge unavailable (e.g. browser preview) — clear guard so a
      // future native context can retry.
      registeredUserIdRef.current = null;
      try { sessionStorage.removeItem(sessionKey); } catch {}
      console.error(err);
    });
  }, [user?.id]);

  // Handle notification taps: native bridges (Despia/OneSignal) post a
  // message or set window.__notificationData with the notification payload.
  // We route based on data.event_id or data.conversation_id so taps open the
  // correct screen instead of just launching the app.
  useEffect(() => {
    const handlePayload = (raw: unknown) => {
      try {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!data || typeof data !== 'object') return;
        const payload = (data as any).data ?? data;
        const eventId = payload?.event_id;
        const conversationId = payload?.conversation_id;
        if (eventId && typeof eventId === 'string') {
          navigate(`/events/${eventId}`);
        } else if (conversationId && typeof conversationId === 'string') {
          navigate(`/messages/${conversationId}`);
        }
      } catch (err) {
        console.warn('[notification-open] failed to parse payload', err);
      }
    };

    const onMessage = (event: MessageEvent) => {
      const d = event.data;
      if (!d) return;
      if (typeof d === 'object' && (d.notificationOpened || d.onesignal || d.notification)) {
        handlePayload(d.notification ?? d.notificationOpened ?? d);
      } else if (typeof d === 'string' && d.includes('event_id')) {
        handlePayload(d);
      }
    };

    window.addEventListener('message', onMessage);

    // Despia/native bridge can stash the launch payload on window before React mounts.
    const launch = (window as any).__notificationData;
    if (launch) {
      handlePayload(launch);
      try { delete (window as any).__notificationData; } catch {}
    }

    // Custom event dispatched by native bridge wrappers
    const onCustom = (e: Event) => handlePayload((e as CustomEvent).detail);
    window.addEventListener('notificationOpened', onCustom as EventListener);

    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('notificationOpened', onCustom as EventListener);
    };
  }, [navigate]);

  return (
    <main className="min-h-screen">
      <AppUpdateToast isVisible={updateAvailable} isForceUpdate={forceUpdate} />
      <ChunkErrorBoundary>
        <RouteErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>

          {/* Public Routes (no auth required) */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
          
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
          <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
          <Route path="/herds" element={<ProtectedRoute><Herds /></ProtectedRoute>} />
          <Route path="/herds/:herdId" element={<ProtectedRoute><HerdDetail /></ProtectedRoute>} />
          <Route path="/herds/:herdId/thread/:threadId" element={<ProtectedRoute><HerdThread /></ProtectedRoute>} />
          <Route path="/events/:eventId" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
          <Route path="/me" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/connections" element={<Navigate to="/social" replace />} />
          <Route path="/profile/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
          <Route path="/create-herd-request" element={<ProtectedRoute><CreateHerdRequest /></ProtectedRoute>} />
          <Route path="/host-event-request" element={<ProtectedRoute><HostEventRequest /></ProtectedRoute>} />
          <Route path="/social" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
          <Route path="/friends" element={<Navigate to="/social" replace />} />
          <Route path="/messages" element={<Navigate to="/social?tab=messages" replace />} />
          <Route path="/messages/:conversationId" element={<ProtectedRoute><DirectConversation /></ProtectedRoute>} />
          <Route path="/photos" element={<ProtectedRoute><Photos /></ProtectedRoute>} />
          <Route path="/photos/:photoId" element={<ProtectedRoute><PhotoDetail /></ProtectedRoute>} />
          <Route path="/support" element={<Support />} />
          <Route path="/hall-of-fame" element={<ProtectedRoute><HallOfFame /></ProtectedRoute>} />
          <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
          <Route path="/account-suspended" element={<AccountSuspended />} />
          <Route path="/w/:token" element={<WaiverRecordPage />} />
          <Route path="/g/:eventId" element={<GuestEventLanding />} />
          <Route path="/g/:eventId/success" element={<GuestEventSuccess />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="user-requests" element={<AdminUserRequests />} />
            <Route path="event-requests" element={<Navigate to="/admin/user-requests?tab=events" replace />} />
            <Route path="herds" element={<Navigate to="/admin/user-requests?tab=herds" replace />} />
            <Route path="herd-analytics" element={<AdminHerdAnalytics />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="moderation" element={<AdminModeration />} />
            <Route path="security" element={<AdminSecurityEvents />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="notification-delivery" element={<AdminNotificationDelivery />} />
            <Route path="scrapers" element={<AdminScrapers />} />
            <Route path="logs" element={<AdminLogs />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="paid-events" element={<AdminPaidEvents />} />
            <Route path="photos" element={<AdminPhotos />} />
            <Route path="data-audit" element={<AdminDataAudit />} />
            <Route path="waivers" element={<AdminWaivers />} />
            <Route path="waiver-records" element={<AdminWaiverRecords />} />
            <Route path="staged-events" element={<Navigate to="/admin/events" replace />} />
          </Route>
          
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </ChunkErrorBoundary>

      {showTabBar && <TabBar />}
      {/* AI Chatbot temporarily disabled - uncomment to re-enable */}
      {/* {showTabBar && <MascotChatButton />} */}
    </main>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <GuestProvider>
            <MembershipProvider>
              <LocationPermissionProvider>
                <TourProvider>
                  <AppBootWatchdog />
                  <AppContent />
                  <TourPromptDialog />
                  <SpotlightOverlay />
                  <TaskGuideOverlay />
                </TourProvider>
              </LocationPermissionProvider>
            </MembershipProvider>
          </GuestProvider>
        </AuthProvider>
      </BrowserRouter>

    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
